import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useContext } from 'react';

import { Ranking } from '@flipster/apis/v2/trading-competition/types';
import { useRouter } from '@flipster/i18n/routing';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import { css } from '@flipster/styled-system/css';
import { center, flex, grid, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { ImgOrLottie, contentfulImageUrl } from '../../../../../contentful/assets';
import { useAuth } from '../../../../../shared/auth/state';
import { CompetitionIdContext, competitionBunja, competitionTabsBunja } from '../../../state';
import { rankingStyle } from './ranking.style';

export const TableRow = ({ item, index }: { item: Ranking; index: number }) => {
  const id = useContext(CompetitionIdContext);
  const { competitionContentful } = useBunja(competitionBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const router = useRouter();
  const { user } = useAuth();

  const isRoi = selectedEventTabValue === 'roi';

  const getIconUrl = (rank: number) => {
    const url = {
      1: competitionContentful?.rank1Icon?.url,
      2: competitionContentful?.rank2Icon?.url,
      3: competitionContentful?.rank3Icon?.url,
    }[rank];
    return contentfulImageUrl(url ?? '');
  };

  const isTop3 = item.rank && item.rank <= 3;

  return (
    <li
      key={item.rank}
      data-user-id={item.userId}
      className={css(
        grid.raw({
          columns: 3,
          alignItems: 'center',
          w: '100%',
          h: 60,
          px: 24,
          rounded: 8,
          cursor: 'pointer',
          _hover: {
            bg: 'background.interaction.hover',
          },
        }),
        rankingStyle.raw({ rank: isTop3 ? (item.rank as 1 | 2 | 3) : 'default' }),
        user?.userId === item.userId && {
          borderColor: 'border.default.level-1',
          borderWidth: '1px',
          borderStyle: 'solid',
          bg: 'background.default.level-1',
        },
      )}
      onClick={() => {
        segment.track(CompetitionsEvent.CompetitionTradingLeaderboardListClick, {
          competition_id: id,
          list_order: index,
          nickname: item.nickname ?? '',
        });
        router.push(`/user/profile/${item.userProfileId}`);
      }}
    >
      <div className={hstack({ gap: 12 })}>
        {isTop3 ? (
          <ImgOrLottie url={getIconUrl(item.rank as 1 | 2 | 3)} alt={`rank${item.rank}icon`} css={{ w: 40, h: 40 }} />
        ) : (
          <span
            className={center({
              w: 28,
              h: 28,
              color: 'content.default.level-2',
              textStyle: 'Text/M/Monospace/Strong',
            })}
          >
            {item.rank}
          </span>
        )}
        <button
          type='button'
          className={flex({
            align: 'center',
            gap: 2,
            color: 'content.default.level-5',
            textStyle: 'Text/L/Strong',
          })}
        >
          <span>{item.nickname ?? 'Nickname'}</span>
          {item.profileVisibility === 'PRIVATE' && <LockClosedIcon size={14} />}
          {item.profileVisibility === 'PUBLIC' && (
            <ChevronRightIcon
              size={14}
              color={isTop3 ? token('colors.inverse.white') : token('colors.content.default.level-1')}
            />
          )}
        </button>
      </div>
      <Formatted.Number
        value={item.prize ?? '0'}
        css={{
          textStyle: 'Text/L/Monospace/Regular',
          textAlign: 'right',
          color: Number(item.prize ?? '0') > 0 ? 'content.accent.VIP' : 'content.default.level-5',
        }}
      />
      {isRoi ? (
        <Formatted.Percent
          value={item.value}
          isRate
          toFixed
          withPlus
          withColor
          css={{ textStyle: 'Text/L/Monospace/Regular', justifyContent: 'flex-end', color: 'content.default.level-5' }}
        />
      ) : (
        <Formatted.Number
          value={item.value ?? '0'}
          toFixed
          precision={2}
          withPlus
          withColor
          css={{ textStyle: 'Text/L/Monospace/Regular', textAlign: 'right', color: 'content.default.level-5' }}
        />
      )}
    </li>
  );
};
